import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSponsors } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Handshake, ExternalLink, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SponsorsPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await getSponsors();
        setSponsors(response.data);
      } catch (error) {
        console.error('Error fetching sponsors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'gold': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'silver': return 'bg-gray-400/20 text-gray-300 border-gray-400/30';
      default: return 'bg-amber-700/20 text-amber-600 border-amber-700/30';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'platinum': return 'Platin';
      case 'gold': return 'Gold';
      case 'silver': return 'Silber';
      default: return 'Bronze';
    }
  };

  const sponsorsList = sponsors.filter(s => (s.type || 'sponsor') === 'sponsor');
  const partnersList = sponsors.filter(s => s.type === 'partner');

  const renderCard = (item) => (
    <Card
      key={item.sponsor_id}
      className="card-surface hover:border-primary/30 transition-all"
      data-testid={`sponsor-card-${item.sponsor_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          {item.logo_url ? (
            <img
              src={`${API}/api/files/${item.logo_url}`}
              alt={item.name}
              className="w-28 h-28 object-contain rounded-sm bg-white/5 p-2"
            />
          ) : (
            <div className="w-28 h-28 bg-white/10 rounded-sm flex items-center justify-center">
              <Handshake className="w-12 h-12 text-primary" />
            </div>
          )}
          <Badge className={getTierColor(item.tier)}>
            {getTierLabel(item.tier)}
          </Badge>
        </div>
        <h3 className="text-xl font-heading font-bold uppercase mb-2">
          {item.name}
        </h3>
        {item.description && (
          <p className="text-text-secondary text-sm mb-4">{item.description}</p>
        )}
        {item.website && (
          <a
            href={item.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent hover:underline text-sm"
          >
            Website besuchen
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="sponsors-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            Sponsoren & <span className="text-primary">Partner</span>
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Unsere Sponsoren und Partner machen unsere Vereinsarbeit möglich.
            Werden auch Sie Teil unserer Erfolgsgeschichte!
          </p>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="flex items-center gap-3 mb-8">
            <Handshake className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter">
              Unsere Sponsoren
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="card-surface animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-16 bg-white/10 rounded w-16 mb-4" />
                    <div className="h-6 bg-white/10 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-white/10 rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : sponsorsList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sponsorsList.map(renderCard)}
            </div>
          ) : (
            <div className="text-center py-12 card-surface rounded-lg">
              <Handshake className="w-12 h-12 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">Noch keine Sponsoren eingetragen.</p>
            </div>
          )}
        </div>
      </section>

      {/* Partners Section */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <div className="flex items-center gap-3 mb-8">
            <Users className="w-6 h-6 text-accent" />
            <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter">
              Unsere Partner
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="card-surface animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-16 bg-white/10 rounded w-16 mb-4" />
                    <div className="h-6 bg-white/10 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : partnersList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partnersList.map(renderCard)}
            </div>
          ) : (
            <div className="text-center py-12 card-surface rounded-lg">
              <Users className="w-12 h-12 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">Noch keine Partner eingetragen.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA to Contact */}
      <section className="section-padding">
        <div className="container-max text-center">
          <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter mb-4">
            Sponsor oder Partner werden
          </h2>
          <p className="text-text-secondary mb-6">
            Interessiert an einer Partnerschaft? Kontaktiere uns!
          </p>
          <Link to="/kontakt" className="btn-primary inline-block px-8 py-3" data-testid="sponsors-contact-cta">
            Zum Kontaktformular
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SponsorsPage;
